import { Injectable } from '@angular/core';
import { LinhaDesempenho } from '../models/nota-types';

/**
 * Serviço responsável por filtros e busca de linhas de desempenho
 * Separação de responsabilidades do DesempenhoEscolarComponent
 */
@Injectable({
  providedIn: 'root'
})
export class DesempenhoFiltersService {
  applyFiltersToLinhas(
    linhas: LinhaDesempenho[],
    selectedAluno: LinhaDesempenho | null,
    searchKeywords: string,
    itemEducacionalId: number | null
  ): LinhaDesempenho[] {
    let filtered = linhas;

    if (selectedAluno) {
      filtered = this.filterBySelectedAluno(filtered, selectedAluno);
    }

    if (this.hasSearchKeywords(searchKeywords)) {
      filtered = this.filterBySearchKeywords(filtered, searchKeywords);
    }

    if (this.shouldFilterByItemEducacional(itemEducacionalId)) {
      filtered = this.filterByItemEducacional(filtered, itemEducacionalId!);
    }

    this.enrichLinhasWithSituacao(filtered);

    return filtered;
  }

  private filterBySelectedAluno(linhas: LinhaDesempenho[], selectedAluno: LinhaDesempenho): LinhaDesempenho[] {
    return linhas.filter(linha => linha.enturmacao.id === selectedAluno.enturmacao.id);
  }

  private hasSearchKeywords(keywords: string): boolean {
    return !!(keywords && keywords.trim().length > 0);
  }

  private filterBySearchKeywords(linhas: LinhaDesempenho[], keywords: string): LinhaDesempenho[] {
    const searchLower = keywords.toLowerCase().trim();
    return linhas.filter(linha => {
      const nome = (linha.enturmacao.matricula.nome || '').toLowerCase();
      const nomeSocial = (linha.enturmacao.matricula.nomeSocial || '').toLowerCase();
      const alunoNome = (linha.enturmacao.aluno?.nome || '').toLowerCase();
      const alunoMatricula = (linha.enturmacao.aluno?.matricula || '').toLowerCase();
      const numeroChamada = linha.enturmacao.numeroChamada?.toString() || '';

      return nome.includes(searchLower) ||
             nomeSocial.includes(searchLower) ||
             alunoNome.includes(searchLower) ||
             alunoMatricula.includes(searchLower) ||
             numeroChamada.includes(searchLower);
    });
  }

  private shouldFilterByItemEducacional(itemEducacionalId: number | null): boolean {
    return !!(itemEducacionalId !== null && itemEducacionalId !== undefined);
  }

  private filterByItemEducacional(linhas: LinhaDesempenho[], itemEducacionalId: number): LinhaDesempenho[] {
    return linhas.filter(linha => {
      const itensEducacionais = linha.enturmacao.itensEducacionais || [];
      return itensEducacionais.some(item => item.id === itemEducacionalId);
    });
  }

  private enrichLinhasWithSituacao(linhas: LinhaDesempenho[]): void {
    linhas.forEach(linha => {
      const situacao = this.getSituacaoFromEnturmacao(linha.enturmacao);
      linha.situacaoDescription = this.getSituacaoDescription(situacao);
    });
  }

  private getSituacaoFromEnturmacao(enturmacao: LinhaDesempenho['enturmacao']): string | undefined {
    // Ordem de prioridade: item educacional > enturmação > matrícula > matrícula aninhada
    if (enturmacao.situacaoItemEducacional) {
      return enturmacao.situacaoItemEducacional;
    }

    if (enturmacao.situacao) {
      return enturmacao.situacao;
    }

    if (enturmacao.situacaoMatricula) {
      return enturmacao.situacaoMatricula;
    }

    if (enturmacao.matricula?.situacao) {
      return enturmacao.matricula.situacao;
    }

    return undefined;
  }

  private getSituacaoDescription(situacao: string | undefined): string {
    if (!situacao) {
      return '';
    }

    // Fallback manual: situações que não constam nos enums
    const situacoesComuns: Record<string, string> = {
      'CURSANDO': 'Cursando',
      'APROVADO': 'Aprovado',
      'REPROVADO': 'Reprovado',
      'TRANSFERIDO': 'Transferido',
      'EVADIDO': 'Evadido',
      'MATRICULADO': 'Matriculado',
      'ATIVO': 'Ativo',
      'INATIVO': 'Inativo',
      'CANCELADO': 'Cancelado'
    };

    return situacoesComuns[situacao] || situacao;
  }
}

